import { Button, Chip, Dialog, Field, Input, Select, Tray } from '@aurea/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { AlertTriangle, Hourglass, Plus } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import { useErrorText } from '../../lib/i18n';
import { fmtMoney, fmtShortDate } from '../../lib/time';
import { isWritable, meQuery, useMe } from '../_app';

export const Route = createFileRoute('/_app/packages')({
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQuery);
    if (!me.roles.includes('admin')) throw redirect({ to: '/' });
  },
  component: Packages,
});

function Packages() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const qc = useQueryClient();
  const templates = useQuery({ queryKey: ['package-templates'], queryFn: api.packageTemplates });
  const attention = useQuery({ queryKey: ['billing', 'attention'], queryFn: api.billingAttention });
  const [creating, setCreating] = useState(false);
  const writable = isWritable(useMe());
  const archive = useMutation({
    mutationFn: api.archivePackageTemplate,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['package-templates'] }),
  });

  return (
    <>
      <header className="page-head">
        <h1>{t('billing.title')}</h1>
        <div className="flex-1" />
        {writable && (
          <Button onClick={() => setCreating(true)}>
            <Plus className="i" />
            {t('billing.newTemplate')}
          </Button>
        )}
      </header>

      <div className="today-layout">
        <section aria-labelledby="tpl-h">
          <h2 id="tpl-h" className="mb-4 text-xl font-semibold">
            {t('billing.templates')}
          </h2>
          {templates.data?.length === 0 && (
            <p className="tray tray-pad text-sm text-ink-2">{t('billing.noTemplates')}</p>
          )}
          <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-4">
            {templates.data?.map((x) => (
              <article
                key={x.id}
                className="paper sticker flex min-h-40 flex-col gap-1 p-4"
                data-c={x.color ?? 'sand'}
              >
                <h3 className="font-semibold leading-tight">{x.name}</h3>
                <p className="text-2xl font-bold tracking-tight">{fmtMoney(x.price, lang)}</p>
                <p className="meta">
                  {t('plural.lessons', { count: x.lessonsCount })}
                  {x.validityDays ? ` · ${t('billing.days', { count: x.validityDays })}` : ''}
                </p>
                <p className="meta">{x.scopeName ?? t('billing.anyLesson')}</p>
                <div className="mt-auto flex items-center gap-2 pt-2">
                  <span className="meta flex-1">{t('billing.sold', { count: x.sold })}</span>
                  <button
                    type="button"
                    className="btn btn-paper-light"
                    hidden={!writable}
                    onClick={() => archive.mutate(x.id)}
                  >
                    {t('billing.archive')}
                  </button>
                </div>
              </article>
            ))}
          </div>
        </section>

        <aside className="today-side flex flex-col gap-4">
          <Tray
            title={t('billing.expiring')}
            icon={<Hourglass className="i" />}
            count={attention.data?.expiring.length ?? 0}
          >
            {attention.data?.expiring.length === 0 && (
              <p className="text-sm text-ink-3">{t('billing.expiringEmpty')}</p>
            )}
            <ul className="rows">
              {attention.data?.expiring.map((e) => (
                <li key={`${e.studentId}${e.packageName}`}>
                  <Chip color={e.color ?? 'sand'} />
                  <div className="min-w-0 flex-1">
                    <Link
                      to="/people/$id"
                      params={{ id: e.studentId }}
                      className="block truncate font-semibold hover:underline"
                    >
                      {e.studentName}
                    </Link>
                    <span className="text-xs text-ink-3">
                      {e.packageName}
                      {e.validUntil && ` · ${t('billing.until', { date: fmtShortDate(e.validUntil, lang) })}`}
                    </span>
                  </div>
                  <span className={e.left === 0 ? 'font-semibold text-danger' : 'font-semibold'}>
                    {t('billing.left', { left: e.left, total: e.total })}
                  </span>
                </li>
              ))}
            </ul>
          </Tray>
          <Tray
            title={t('billing.debtors')}
            icon={<AlertTriangle className="i" />}
            count={attention.data?.debtors.length ?? 0}
          >
            {attention.data?.debtors.length === 0 && (
              <p className="text-sm text-ink-3">{t('billing.debtorsEmpty')}</p>
            )}
            <ul className="rows">
              {attention.data?.debtors.map((d) => (
                <li key={d.studentId}>
                  <div className="min-w-0 flex-1">
                    <Link
                      to="/people/$id"
                      params={{ id: d.studentId }}
                      className="block truncate font-semibold hover:underline"
                    >
                      {d.studentName}
                    </Link>
                    <span className="text-xs text-ink-3">
                      {[
                        d.debtLessons > 0 &&
                          t('billing.debtLessons', {
                            lessons: t('plural.lessons', { count: d.debtLessons }),
                          }),
                        d.balance < 0 && t('billing.owes', { amount: fmtMoney(-d.balance, lang) }),
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </Tray>
        </aside>
      </div>
      {creating && <TemplateDialog onClose={() => setCreating(false)} />}
    </>
  );
}

function TemplateDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const errorText = useErrorText();
  const qc = useQueryClient();
  const disciplines = useQuery({ queryKey: ['disciplines'], queryFn: api.disciplines });
  const courses = useQuery({ queryKey: ['courses'], queryFn: api.courses });
  const create = useMutation({
    mutationFn: api.createPackageTemplate,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['package-templates'] });
      onClose();
    },
  });
  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const scope = String(f.get('scope') ?? '');
    const [kind, id] = scope.split(':');
    create.mutate({
      name: String(f.get('name')).trim(),
      ...(kind === 'd' ? { disciplineId: id } : kind === 'c' ? { courseId: id } : {}),
      lessonsCount: Number(f.get('lessons')),
      price: Math.round(Number(f.get('price')) * 100),
      ...(f.get('validity') ? { validityDays: Number(f.get('validity')) } : {}),
    });
  };
  return (
    <Dialog open onClose={onClose} title={t('billing.newTemplate')} strip="apricot">
      <form className="flex flex-col gap-4 pb-6" onSubmit={submit}>
        <Field label={t('billing.templateName')} hint={t('billing.templateNameHint')}>
          <Input name="name" required autoFocus maxLength={120} />
        </Field>
        <Field label={t('billing.appliesTo')}>
          <Select name="scope" defaultValue="">
            <option value="">{t('billing.anyLesson')}</option>
            <optgroup label={t('courses.disciplines')}>
              {disciplines.data?.map((d) => (
                <option key={d.id} value={`d:${d.id}`}>
                  {d.name}
                </option>
              ))}
            </optgroup>
            <optgroup label={t('courses.title')}>
              {courses.data?.map((c) => (
                <option key={c.id} value={`c:${c.id}`}>
                  {c.name}
                </option>
              ))}
            </optgroup>
          </Select>
        </Field>
        <div className="grid grid-cols-3 gap-3">
          <Field label={t('billing.lessonsCount')}>
            <Input name="lessons" type="number" min={1} max={500} defaultValue={8} required />
          </Field>
          <Field label={t('billing.price')}>
            <Input name="price" type="number" min={0} step="any" required />
          </Field>
          <Field label={t('billing.validity')}>
            <Input name="validity" type="number" min={1} max={730} defaultValue={35} />
          </Field>
        </div>
        {create.isError && <p className="text-sm text-danger">{errorText(create.error)}</p>}
        <div className="flex gap-3">
          <Button type="submit" loading={create.isPending}>
            {t('common.create')}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
